// Smart-input parsing for the DTC & Status Byte Decoder. Accepts a DTC string
// ("P0301", "P0A1F-1C") or raw bytes in common hex spellings, with an optional CAN ID
// prefix and ISO-TP single-frame length byte, and decides which decoder applies.
// Anything ambiguous is returned as such (with alternatives) — never guessed silently.
import { describeCanId, hex2, hex3, bytesToHex } from './constants';
import { parseDtcString } from './dtc';

const HEX_BYTE_RE = /^[0-9a-f]{1,2}$/i;

const RESPONSE_SIDS = new Set([0x41, 0x43, 0x47, 0x4a, 0x49, 0x59, 0x54, 0x7f]);
const OBD_RESPONSE_SIDS = new Set([0x41, 0x43, 0x47, 0x4a, 0x49]);
const OBD_ONE_BYTE_RESPONSES = {
  0x43: 'OBD Mode 03 response with no DTCs (pre-CAN form)',
  0x44: 'OBD Mode 04 positive response (DTCs cleared)',
  0x47: 'OBD Mode 07 response with no DTCs (pre-CAN form)',
  0x4a: 'OBD Mode 0A response with no DTCs (pre-CAN form)',
};
const OBD_REQUEST_NAMES = { 0x03: 'Mode 03 — request stored DTCs', 0x04: 'Mode 04 — clear DTCs and stored values', 0x07: 'Mode 07 — request pending DTCs', 0x0a: 'Mode 0A — request permanent DTCs' };

const fail = (error) => ({ ok: false, error });

// Splits raw text into tokens and an optional CAN ID prefix.
function tokenize(raw) {
  const cleaned = raw.trim().replace(/#/g, ' ').replace(/[,;]/g, ' ');
  const parts = cleaned.split(/[\s_-]+/).map((p) => p.replace(/:$/, '').replace(/^0x/i, '')).filter(Boolean);
  let canId = null;
  let tokens = parts;
  if (parts.length > 1) {
    const first = parts[0];
    if (/^[0-7][0-9a-f]{2}$/i.test(first)) {
      canId = parseInt(first, 16);
      tokens = parts.slice(1);
    } else if (/^18d[ab][0-9a-f]{4}$/i.test(first)) {
      canId = parseInt(first, 16);
      tokens = parts.slice(1);
    }
  }
  const bytes = [];
  for (const t of tokens) {
    if (/^[0-9a-f]+$/i.test(t) && t.length > 2) {
      if (t.length % 2 !== 0) return { error: `"${t}" has an odd number of hex digits (${t.length}) — bytes must be 2 hex digits each.` };
      t.match(/.{2}/g).forEach((pair) => bytes.push(parseInt(pair, 16)));
      continue;
    }
    if (!HEX_BYTE_RE.test(t)) {
      return { error: `"${t}" is not a valid hex byte. Use hex digits 0-9/A-F, e.g. "59 02 FF" or "0x59,0x02,0xFF". A DTC code looks like "P0301" or "P0A1F-1C".` };
    }
    bytes.push(parseInt(t, 16));
  }
  if (!bytes.length) return { error: 'No bytes found in the input.' };
  return { bytes, canId };
}

// Strips an ISO-TP single-frame PCI byte. With a CAN ID we expect one; without, only
// strip when the next byte is clearly a service ID and the length fits.
function stripIsoTp(bytes, hasCanId) {
  const pci = bytes[0];
  const type = pci >> 4;
  if (hasCanId) {
    if (type === 1) return { error: 'This CAN frame is an ISO-TP First Frame (PCI 1x) — the message continues in Consecutive Frames. Paste the reassembled payload (without PCI bytes) instead.' };
    if (type === 2) return { error: 'This CAN frame is an ISO-TP Consecutive Frame (PCI 2x) — part of a longer message. Paste the reassembled payload instead.' };
    if (type === 3) return { error: 'This CAN frame is an ISO-TP Flow Control frame (PCI 3x) — it carries no diagnostic data.' };
  }
  if (type !== 0) return { bytes, isoTp: null };
  const len = pci & 0x0f;
  if (len === 0 || bytes.length < 2) return { bytes, isoTp: null };
  if (!hasCanId && !(RESPONSE_SIDS.has(bytes[1]) || ((bytes[1] === 0x19 || bytes[1] === 0x14) && bytes.length === len + 1))) return { bytes, isoTp: null };
  if (len > bytes.length - 1) {
    if (hasCanId) return { error: `ISO-TP single-frame length byte says ${len} data bytes, but only ${bytes.length - 1} follow — the frame is truncated.` };
    return { bytes, isoTp: null };
  }
  const padding = bytes.slice(1 + len);
  return { bytes: bytes.slice(1, 1 + len), isoTp: { length: len, padding } };
}

const dtcAlternative = (n) => (n >= 2 && n <= 4 ? [{ label: n === 4 ? 'Decode as DTC + status byte instead' : 'Decode as a DTC instead', force: 'dtc' }] : []);

function routeBytes(p, force) {
  const n = p.length;
  if (force === 'dtc') {
    if (n === 2) return { kind: 'dtcBytes', tab: 'dtc', dtcBytes: [p[0], p[1]], ftb: null };
    if (n === 3) return { kind: 'dtcBytes', tab: 'dtc', dtcBytes: p, ftb: p[2] };
    if (n === 4) return { kind: 'dtcBytes', tab: 'dtc', dtcBytes: p.slice(0, 3), ftb: p[2], status: p[3] };
    return fail(`A DTC is 2 bytes (OBD) or 3 bytes (UDS, + optional status byte); got ${n}.`);
  }
  if (force === 'status') return { kind: 'status', tab: 'status', status: p[0] };
  if (force === 'ftb') return { kind: 'ftb', tab: 'dtc', ftb: p[0] };
  if (force === 'obd') return { kind: 'obd', tab: 'obd', bytes: p };
  if (force === 'uds') return { kind: 'uds', tab: 'uds', bytes: p };

  const sid = p[0];
  // A single byte is always ambiguous: offer the readings, never pick one silently.
  if (n === 1) {
    const options = [
      { label: 'DTC status byte', force: 'status' },
      { label: 'Failure Type Byte (FTB)', force: 'ftb' },
    ];
    if (OBD_REQUEST_NAMES[sid]) options.push({ label: `OBD request: ${OBD_REQUEST_NAMES[sid]}`, force: 'obd' });
    if (OBD_ONE_BYTE_RESPONSES[sid]) options.push({ label: OBD_ONE_BYTE_RESPONSES[sid], force: 'obd' });
    if (sid === 0x54) options.push({ label: 'UDS 0x14 positive response (DTCs cleared)', force: 'uds' });
    return { kind: 'ambiguousByte', tab: null, value: sid, options };
  }
  if (sid === 0x7f) {
    if (n < 3) return fail(`Incomplete negative response — 7F needs <Service ID> <NRC> after it (3 bytes total); got ${n}.`);
    return { kind: 'nrc', tab: null, sid: p[1], nrc: p[2], extra: n > 3 ? p.slice(3) : [] };
  }
  if (sid === 0x59 || sid === 0x19 || sid === 0x14) {
    return { kind: 'uds', tab: 'uds', bytes: p, alternatives: dtcAlternative(n) };
  }
  if (OBD_RESPONSE_SIDS.has(sid)) {
    return { kind: 'obd', tab: 'obd', bytes: p, alternatives: dtcAlternative(n) };
  }
  if (sid === 0x01 && n >= 2 && n <= 7) return { kind: 'obd', tab: 'obd', bytes: p, alternatives: dtcAlternative(n) };
  if (sid === 0x09 && n === 2) return { kind: 'obd', tab: 'obd', bytes: p, alternatives: dtcAlternative(n) };
  if (n === 2) return { kind: 'dtcBytes', tab: 'dtc', dtcBytes: p, ftb: null };
  if (n === 3) return { kind: 'dtcBytes', tab: 'dtc', dtcBytes: p, ftb: p[2] };
  if (n === 4) return { kind: 'dtcBytes', tab: 'dtc', dtcBytes: p.slice(0, 3), ftb: p[2], status: p[3] };
  return fail(`Couldn't identify ${n} bytes starting with 0x${hex2(sid)}. Expected a UDS 0x19 response (59 …) or request (19 …), a 0x14 clear (14 FF FF FF), an OBD-II response (41/43/47/4A/49 …), a DTC (2–3 bytes, optionally + status), or a single status byte.`);
}

// parseDtcInput(raw, { force }) -> { ok, kind, tab, ... } — see routeBytes for kinds.
export function parseDtcInput(raw, { force } = {}) {
  const text = (raw || '').trim();
  if (!text) return fail('Enter a DTC (P0301), a status byte (2F), or a UDS / OBD-II response (59 02 FF …, 41 0C 1A F8).');

  // A DTC string — unless it is also plain even-length hex (e.g. "C10000"), which is
  // read as bytes with the DTC reading offered as an alternative.
  const compact = text.replace(/^0x/i, '');
  const dtc = parseDtcString(compact);
  const plainHex = /^[0-9a-f]+$/i.test(compact) && compact.length % 2 === 0;
  if (dtc && !plainHex) {
    if (dtc.error) return fail(dtc.error);
    return { ok: true, kind: 'dtcCode', tab: 'dtc', code: dtc.code, ftb: dtc.ftb, canInfo: null, isoTp: null, alternatives: [] };
  }
  if (/^[PCBU][0-3][0-9A-F]{3}\s*[-:]/i.test(compact)) {
    return fail(`"${text}" — the Failure Type Byte after the code must be exactly 2 hex digits, e.g. P0A1F-1C.`);
  }
  if (/^[PCBU][0-9A-F]{1,}$/i.test(compact) && /^[PU]/i.test(compact)) {
    return fail(`"${text}" looks like a DTC but is not valid — expected a letter P/C/B/U, a digit 0-3, then 3 hex digits (e.g. P0301), optionally followed by -XX for the Failure Type Byte.`);
  }

  const tk = tokenize(text);
  if (tk.error) return fail(tk.error);
  let { bytes } = tk;
  const { canId } = tk;
  const canInfo = canId !== null ? describeCanId(canId) : null;
  let isoTp = null;
  if (force !== 'noIsoTp') {
    const st = stripIsoTp(bytes, canId !== null);
    if (st.error) return fail(st.error);
    bytes = st.bytes;
    isoTp = st.isoTp;
  }
  const routed = routeBytes(bytes, force === 'noIsoTp' ? undefined : force);
  if (routed.ok === false) return routed;

  const warnings = [];
  if (canInfo && bytes.length) {
    const isResponse = bytes[0] >= 0x40;
    if (canInfo.role === 'request' && isResponse) warnings.push(`CAN ID ${canId > 0x7ff ? canId.toString(16).toUpperCase() : hex3(canId)} is a request ID, but the payload looks like a response.`);
    if (canInfo.role === 'response' && !isResponse) warnings.push(`CAN ID ${canId > 0x7ff ? canId.toString(16).toUpperCase() : hex3(canId)} is a response ID, but the payload looks like a request.`);
  }
  const alternatives = [...(routed.alternatives || [])];
  if (isoTp && canId === null) alternatives.push({ label: `Don't treat 0x${hex2(isoTp.length)} as an ISO-TP length byte`, force: 'noIsoTp' });
  if (plainHex && dtc && !dtc.error) alternatives.push({ label: `Decode as DTC code ${dtc.code}${dtc.ftb !== null ? `-${hex2(dtc.ftb)}` : ''} instead`, forceText: `${dtc.code}${dtc.ftb !== null ? `-${hex2(dtc.ftb)}` : ''}` });

  return { ok: true, ...routed, bytes, canId, canInfo, isoTp, warnings, alternatives, normalized: bytesToHex(bytes) };
}

export const EXAMPLE_INPUTS = [
  { label: 'P0301', value: 'P0301' },
  { label: 'P0A1F-1C', value: 'P0A1F-1C' },
  { label: 'C1 00 00', value: 'C1 00 00' },
  { label: 'Status 2F', value: '2F' },
  { label: 'UDS 0x19 02', value: '59 02 FF 03 01 00 2F C1 00 00 08' },
  { label: 'UDS 0x19 01', value: '59 01 FF 01 00 02' },
  { label: 'Clear 14', value: '14 FF FF FF' },
  { label: 'RPM', value: '41 0C 1A F8' },
  { label: '7E8 speed', value: '7E8 03 41 0D 3C' },
  { label: 'Monitors', value: '41 01 83 07 65 00' },
  { label: 'Mode 03', value: '43 02 01 33 01 43' },
  { label: 'VIN', value: '49 02 01 31 4D 38 47 44 4D 39 41 58 4B 50 30 34 32 37 38 38' },
  { label: '7F 19 31', value: '7F 19 31' },
];
